import React from 'react';
import PropTypes from 'prop-types';
import { ToastContainer } from 'react-toastify';
import Navbar from 'components/PartnerPage/Navbar';
import HeroSectionWithQuickQuote from 'components/PartnerPage/HeroWithQuickQuote'
import Footer from 'components/PartnerPage/Footer';
import ShipOverview from 'components/PartnerPage/ShipOverview';
import FeaturesGrid from 'components/PartnerPage/FeaturesGrid';
import Newsletter from 'components/PartnerPage/Newsletter';
import backgroundImageMobile from 'images/shipplay/backgrounds/mobile/schools-hero-background.jpg';
import backgroundImageDesktop from 'images/shipplay/backgrounds/desktop/schools-hero-background.jpg';
import NavLogo from 'images/shipplay/logos/Ship&Play_FullColor_DarkBG.svg';
import FooterLogo from 'images/shipplay/logos/Ship&Play_FullColor_DarkBG.svg';
import ShipPlayLogo from 'images/shipplay/logos/shipplay-logo.png';

const backgroundImageStyles = {
  '--background-image-mobile': `linear-gradient(
    rgba(0, 0, 0, 0.7),
    rgba(0, 0, 0, 0)), url(${backgroundImageMobile})`,
  '--background-image-desktop': `linear-gradient(
    rgba(0, 0, 0, 0.7),
    rgba(0, 0, 0, 0)), url(${backgroundImageDesktop})`,
}

const imageModules = import.meta.glob('images/shipplay/schools/*.png', { eager: true });

const getImageUrl = (imageName) => {
  const imagePath = `/images/shipplay/schools/${imageName}.png`;
  const image = imageModules[`${imagePath}`];
  return image?.default;
};

const generateCTAPath = () => {
  return '/ship';
};

function ShipplayCollegeMovingServicesPage({ quickQuoteTitle, termsConditionsText }) {
  return (
    <>
      <ToastContainer />
      {/* Background image container with responsive height */}
      <div style={backgroundImageStyles} className="relative font-poppins bg-cover bg-center backgroundImage">
        <Navbar NavLogo={NavLogo}
                logoClasses="w-48"
                customCTAPath={generateCTAPath()}
                showExtraLinks={false}
        />
        <HeroSectionWithQuickQuote
          header="College move-in day made easy"
          subheader="
            Ship boxes, luggage, and sports equipment to school.
          "
          quickQuoteTitle={quickQuoteTitle}
          breakText="Rates starting at $49"
          buttonText="Book in less than two minutes"
        />

      </div>

      <main className="flex flex-col items-center justify-between w-full font-poppins">
        <ShipOverview
          buttonCTAPath={generateCTAPath()}
          reasons={[{
            title: 'Why Ship?',
            description: `
              Moving to a new school is a big transition. Ship ahead so you don't 
              have to worry about hauling boxes and cramming suitcases into your 
              car (or paying extra for them at the airport).
            `,
          }, {
            title: 'How it Works',
            description: `
              Our school shipping services make the move easy. Just schedule a pickup, 
              pack your bags, and we handle the rest. Experience seamless door-to-door 
              delivery from home to your dorm.
            `,
          }, {
            title: 'What to Expect',
            description: `
              Affordable pricing, on-time delivery, and top-notch service are guaranteed. 
              No hidden fees, no hassle. Just smooth, reliable shipping for all your prized 
              possessions.
            `,
          },
        ]}>
          <p className="text-[28px] lg:text-[32px] lg:leading-[58px] font-bold text-[#1C2938] mb-5">
            Why <img src={ShipPlayLogo} alt="Ship & Play Logo" className="inline-block w-[166px] ml-2" />?
          </p>
          <p className="text-[#231F20] text-lg leading-[30px]">
            The start of a new school year means one thing: packing, and lots of it. 
            From your collection of concert tees to that set of golf clubs you whip 
            out on weekends, you deserve to bring the comforts of home with you.
          </p>
          <p className="text-[#231F20] text-lg leading-[30px] mt-7">
            Ship&Play, now offering shipping to schools, is a hassle-free college moving 
            service that allows you to do just that, without all the heavy lifting. 
            We’ll pick up your boxes and bags right from your doorstep and deliver 
            them directly to campus or back home at the end of the year.
          </p>
        </ShipOverview>
        <FeaturesGrid
          header="What Sets Us Apart"
          features={[
            {
              image: getImageUrl('computer'),
              title: 'Affordable Rates',
              description: `
                From tuition to textbooks, we know budgeting for college is important. That’s 
                why our customers benefit from prices up to 60% lower than regular carrier costs.
              `
            },
            {
              image: getImageUrl('boxes'),
              title: 'On-Time Guarantee',
              description: `
                Our college moving expertise means your belongings are in good hands. And our on-time 
                guarantee gives you peace of mind every step of the way.
              `
            },
            {
              image: getImageUrl('books'),
              title: 'Complimentary Insurance',
              description: `
                Free coverage is included with every shipment. Plus, you can select higher coverage 
                options to match the value of your items.
              `
            },
            {
              image: getImageUrl('phone_call'),
              title: 'Personal Service',
              description: `
                To our team, you're not just another customer - you're our most important one. Enjoy 
                personal customer service seven days a week via phone, email, text, or live chat.
              `
            },
            {
              image: getImageUrl('texting'),
              title: 'Real-Time Tracking',
              description: `
                Our real-time tracking feature allows you to monitor the progress of your shipment from pickup 
                to delivery - anytime and anywhere.
              `
            },
            {
              image: getImageUrl('suv'),
              title: 'Unmatched Convenience',
              description: `
                Moving to college is already overwhelming, both physically and emotionally. Save yourself the 
                stress, and the sweat, and let us make your transition smoother.
              `
            }
        ]} />
      </main>
      <Footer
        FooterLogo={FooterLogo} logoSize="w-40" brand="shipplay"
        shipLinkPath={generateCTAPath()}
      >
        {/* T&C */}
        <div className="text-left pb-8 lg:py-8 pl-[5%] lg:pl-0 gap-12">
          <div className="flex items-center justify-between">
            <h5 className="text-white text-[22px] font-semibold mb-2">Any questions?</h5>
            <span className="hidden lg:block h-[3px] w-4 bg-green-500"></span>
          </div>
          <p className="my-2 text-lg leading-8">
            {termsConditionsText}
          </p>
        </div>
        <Newsletter />
      </Footer>
    </>
  );
}

ShipplayCollegeMovingServicesPage.propTypes = {
  couponCode: PropTypes.string,
  quickQuoteTitle: PropTypes.string,
  termsConditionsText: PropTypes.string
};

export default ShipplayCollegeMovingServicesPage;
